import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useDemoResetFeed } from '../../hooks/useDemoResetFeed';
import { useUserEvents } from '../../hooks/useUserEvents';
import ScopeSync from './ScopeSync';
import NodeOfflineNotice from './NodeOfflineNotice';
import ConnectingOverlay from '../ConnectingOverlay';
import { useNode } from '../../contexts/NodeContext';
import { NodeStreamProvider } from '../../contexts/NodeStreamContext';
import { isNodeScoped } from '../../constants/nodeScopedPaths';
import { TitleBarProvider, AlertPopups, useAlertPopups, type AlertPopup } from '@trinetra/shared';
import { TourProvider } from '../../tours/TourProvider';

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { nodeConnecting, dismissConnecting, selectedNode, selectedNodeId, selectNode } = useNode();
  const { pathname } = useLocation();
  /**
   * The search page wants the width: a filter column, the answer and a tracking dock side by
   * side. So the sidebar folds on the way IN and goes back to however it was on the way OUT —
   * the operator's own choice everywhere else is not overwritten, and the toggle still works on
   * the search page itself.
   */
  const onSearchPage = pathname === '/search' || pathname.startsWith('/search/');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(onSearchPage);
  const collapsedBeforeSearch = useRef<boolean | null>(onSearchPage ? false : null);
  useEffect(() => {
    if (onSearchPage) {
      setSidebarCollapsed((prev) => {
        if (collapsedBeforeSearch.current === null) collapsedBeforeSearch.current = prev;
        return true;
      });
    } else if (collapsedBeforeSearch.current !== null) {
      setSidebarCollapsed(collapsedBeforeSearch.current);
      collapsedBeforeSearch.current = null;
    }
  }, [onSearchPage]);

  // Guard, not just decoration: the sidebar greys these entries out, but a bookmark, the back
  // button, or a node that drops while the page is open all get here without touching the nav.
  // Rendered in place rather than redirected, so the URL survives and the page comes back on
  // its own when the node reconnects.
  // Feeds the title bar's demo banner. Here rather than in the header because this is the
  // component mounted on every authenticated page, and the banner must be right on all of
  // them — the wipe it warns about takes attendance and reports, not just alerts.
  useDemoResetFeed(selectedNodeId ?? null);

  // Dashboard pop-ups: a site's Routing sent an alert to this user. Open = that site's alert.
  const popups = useAlertPopups();
  useUserEvents(popups.push);
  const navigate = useNavigate();
  const openPopup = (p: AlertPopup) => {
    if (p.node_id && p.node_id !== selectedNodeId) selectNode(p.node_id);
    // Raised by the cloud (a site offline, the cloud server): its entry is on the System Alerts page.
    if (p.cloudRaised) { navigate('/system-alerts'); return; }
    // A site's system incident is mirrored on the cloud's System Alerts page (system_alert.view) —
    // a page an operator can open; Alert Actions needs alerts.config.
    navigate(p.kind === 'system' || !p.id ? '/system-alerts' : `/alerts/${p.id}`);
  };

  const blockedByOfflineNode = !!selectedNode && selectedNode.status !== 'online' && isNodeScoped(pathname);

  return (
    <NodeStreamProvider>
    <TitleBarProvider>
    {/* Onboarding tour — inside the shell because it points at the header and sidebar. */}
    <TourProvider>
    <ScopeSync />
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      <Sidebar
        collapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed((prev) => !prev)}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        <Header onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)} />
        {/* No per-page scope banner any more: the title bar names the site permanently, so a
            strip repeating it on every scoped page was duplication — and every page gets that
            vertical space back. */}
        {/* Tight on purpose: the page title is in the title bar now (PageTitle), so the
            padding is only a gutter. PAGE_FILL_H assumes exactly this — change both together.
            Search & Track has none: its filter column sits flush against the side nav and the
            title bar, and it sizes itself with h-full rather than PAGE_FILL_H. */}
        {/* A page whose filter column is flush (PageWithFilters as the page body) takes no
            padding either — the column sits against the nav and the title bar, as on Search. */}
        <main className={`flex-1 overflow-y-auto ${onSearchPage ? 'p-0' : 'p-2 sm:p-3 has-[>[data-filter-page=flush]]:p-0'}`}>
          {blockedByOfflineNode ? <NodeOfflineNotice node={selectedNode!} /> : <Outlet />}
        </main>
      </div>
      {nodeConnecting && selectedNode && (
        <ConnectingOverlay
          message={`Connecting to ${selectedNode.name}…`}
          onSkip={dismissConnecting}
        />
      )}
      <AlertPopups popups={popups.popups} dismiss={popups.dismiss} onOpen={openPopup} />
    </div>
    </TourProvider>
    </TitleBarProvider>
    </NodeStreamProvider>
  );
}
